/* eslint-disable camelcase */
import { useMediaQuery } from '@react-hookz/web';
import clx from 'classnames';

import CustomMarkdown from '@/molecules/CustomMarkdown';
import WidgetCTA from 'components/widgets/components/WidgetCTA';
import { EDITO_WIDGET_X_PADDING } from 'components/widgets/const';
import { ALIGNMENT_ENUM, BACKGROUND_POSITION_TO_CLASSNAME, POSITION_ENUM } from 'types/utils';
import { CMSCallToActionType, CmsImageType } from 'utils/cms/types';

type Props = {
  text?: string;
  title?: string;
  cta?: CMSCallToActionType;
  image?: CmsImageType;
  image_desktop?: CmsImageType;
  hide_image_mobile?: boolean;
  text_position: 'left' | 'right';
  text_alignment: ALIGNMENT_ENUM;
  image_position?: POSITION_ENUM;
  content_list?: {
    icon: string;
    lines: Array<{ line_text: string }> | false;
    lines_position: 'before' | 'after';
  };
  id: string;
};

const ALIGNMENT_TO_CLASSNAME = {
  left: 'self-start items-start text-left',
  center: 'items-center text-center',
  right: 'items-end text-right',
};

const FeaturedContent = ({
  text,
  title,
  cta,
  image,
  image_desktop: imageDesktop,
  hide_image_mobile: hideImageMobile,
  text_position,
  text_alignment,
  image_position = POSITION_ENUM.center,
  content_list: contentList,
  id,
}: Props) => {
  const isDesktop = useMediaQuery('(min-width: 768px)', { initializeWithValue: false });

  return (
    <div className={EDITO_WIDGET_X_PADDING}>
      <div
        className={clx(
          'flex bg-neutral-100 rounded-lg items-center',
          text_position === 'right' ? 'lg:flex-row-reverse  flex-col-reverse' : 'lg:flex-row  flex-col',
        )}
      >
        <div
          className={clx(
            'flex flex-col py-4 px-4 lg:px-6 gap-2',
            image && 'lg:basis-2/3',
            ALIGNMENT_TO_CLASSNAME[text_alignment],
          )}
        >
          {title && <h3 className="title-3 text-neutral-900">{title}</h3>}
          <div
            className={clx(contentList?.lines_position === 'before' ? 'flex-col-reverse' : 'flex-col', 'flex gap-4')}
          >
            {text && (
              <CustomMarkdown
                className={clx('md-medium text-neutral-800 flex flex-col', ALIGNMENT_TO_CLASSNAME[text_alignment])}
              >
                {text}
              </CustomMarkdown>
            )}
            {Array.isArray(contentList?.lines) && (
              <ul className="flex flex-col gap-2">
                {contentList?.lines.map(({ line_text: lineText }) => (
                  <li key={lineText} className="flex md-medium text-neutral-900 gap-2 items-baseline">
                    <i className={clx(contentList.icon, 'text-primary-500 text-sm')} /> {lineText}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {cta?.link && <WidgetCTA className="mt-4" cta={cta} widgetId={id} />}
        </div>
        {image && (
          <div
            className={clx(
              'lg:basis-1/3 self-stretch bg-cover h-[180px] lg:h-auto',
              BACKGROUND_POSITION_TO_CLASSNAME[image_position],
              text_position === 'right'
                ? 'rounded-tr-lg rounded-tl-lg lg:rounded-tr-none lg:rounded-bl-lg'
                : 'rounded-br-lg rounded-bl-lg lg:rounded-bl-none lg:rounded-tr-lg',
              !!hideImageMobile && 'hidden lg:block',
            )}
            style={{
              backgroundImage:
                isDesktop && imageDesktop ? `url(${imageDesktop.sizes.large})` : `url(${image.sizes.large})`,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default FeaturedContent;
